<template>
  <section v-if="schedule"
           class="mt-[40px]">
    <div class="flex items-baseline justify-between">
      <h3 class="text-[18px] text-black mb-[6px] font-semibold">{{ t('space.schedule_title') }}</h3>
      <!-- 綁了 GCal 而且健康才說這句:這是綁定的回報(看到的更準),不是可預約與否的門檻。 -->
      <span v-if="schedule.synced"
            class="text-[13px] text-osn-green-4">🔄 {{ t('space.schedule_synced') }}</span>
    </div>
    <p class="text-[13px] text-osn-grey-01 mb-[10px]">{{ t('space.schedule_note') }}</p>

    <el-calendar v-model="currentDate">
      <template #header="{ date }">
        <el-button-group>
          <el-button size="small"
                     :disabled="prevDisabled"
                     :aria-label="t('space.schedule_prev_month')"
                     @click="goMonth(-1)">‹
          </el-button>
          <el-button size="small"
                     :disabled="nextDisabled"
                     :aria-label="t('space.schedule_next_month')"
                     @click="goMonth(1)">›
          </el-button>
        </el-button-group>
        <span class="font-semibold">{{ date }}</span>
        <el-button size="small"
                   @click="goToday">{{ t('space.schedule_today') }}
        </el-button>
      </template>
      <template #date-cell="{ data }">
        <!-- 上/下個月的格子只佔位:那些日子不在這次查詢的範圍裡,畫圖示等於瞎猜。 -->
        <div v-if="data.type !== 'current-month'"
             class="day-cell text-osn-grey-02">
          <p class="day-number">{{ dayNumber(data.day) }}</p>
        </div>
        <div v-else
             class="day-cell cursor-pointer"
             :class="{'day-selected': data.day === selectedDate}"
             @click="selectDay(data.day)">
          <p class="day-number">{{ dayNumber(data.day) }}</p>
          <p class="day-symbol"
             :class="symbolClass(stateOf(data.day))">{{ stateSymbol(stateOf(data.day)) }}</p>
        </div>
      </template>
    </el-calendar>

    <ul class="legend">
      <li><span class="text-osn-green-4">●</span> {{ t('space.schedule_legend_open') }}</li>
      <li><span class="text-osn-yellow-00">◐</span> {{ t('space.schedule_legend_partial') }}</li>
      <li><span class="text-osn-red-00">✕</span> {{ t('space.schedule_legend_full') }}</li>
      <li><span class="text-osn-grey-02">·</span> {{ t('space.schedule_legend_closed') }}</li>
    </ul>

    <!-- 點一天就地展開:不換頁、不開 dialog,租客不會掉出這一頁。 -->
    <div v-if="!selectedDate"
         class="detail text-osn-grey-01">{{ t('space.schedule_pick_day') }}
    </div>
    <div v-else
         class="detail">
      <div class="flex items-center gap-2 font-semibold">
        <span>{{ selectedLabel }}</span>
        <span :class="symbolClass(selectedState)">{{ stateSymbol(selectedState) }}</span>
      </div>
      <hr class="my-2">

      <template v-if="canInquireOn(selectedState) && selectedSlots.length">
        <p class="text-[14px] text-osn-grey-01 mb-1">{{ t('space.schedule_available') }}</p>
        <ul class="slots">
          <li v-for="slot in selectedSlots"
              :key="slotTimeValue(slot)"
              class="slot"
              :class="slotTimeValue(slot) === selectedSlot ? 'slot-on' : ''"
              @click="selectSlot(slot)">{{ slotLabel(slot) }}
          </li>
        </ul>
        <div class="cta bg-osn-green-4 text-white"
             @click="emitInquire">{{ t('space.schedule_inquire_slot') }}
        </div>
      </template>

      <!-- ✕ 的日子不給「詢問這個場地」:那天的送出必定被後端的閘擋下(IQ0014),
           給了入口等於邀請租客去撞一堵牆。出口改成兩個誠實的選擇:換日期,或問類似場地。 -->
      <template v-else-if="selectedState === 'full'">
        <p class="text-[14px]">{{ t('space.schedule_day_full') }}</p>
        <p class="text-[13px] text-osn-grey-01 mt-1">{{ t('space.schedule_day_full_hint') }}</p>
        <div class="cta border border-osn-green-4 text-osn-green-4"
             @click="emitBroadcast">{{ t('space.schedule_broadcast') }}
        </div>
      </template>

      <!-- closed:那天沒開賣。**不得**說成「已有人租用」——我們並不知道那件事。 -->
      <p v-else
         class="text-[14px] text-osn-grey-01">{{ t('space.schedule_day_closed') }}</p>
    </div>
  </section>
</template>

<script setup
        lang="ts">
import {computed, onMounted, ref, watch} from 'vue'
import {useI18n} from 'vue-i18n'
import {getSpaceSchedule} from '~/api/space'
import {
  canInquireOn,
  dayState,
  indexByDate,
  isNextMonthDisabled,
  isPrevMonthDisabled,
  monthKey,
  monthRange,
  slotLabel,
  slotTimeValue,
  stateSymbol
} from '~/utils/spaceSchedule'
import type {ScheduleDay, ScheduleSlot, ScheduleState, SpaceSchedule} from '~/utils/spaceSchedule'
import {useBehaviorFlow} from '~/composables/useBehaviorFlow'

// BI-UI v2:portal_availability(implementation.md §5)。純瀏覽,無 server 業務成功
// 紀錄(UnsupportedReason),這裡只送互動事件;「plan」step 不在這個元件(方案瀏覽在
// 別處),不在此處猜測送出。
const FEATURE_ID = 'portal_availability'

const props = defineProps<{
  spaceId: number
}>()

const emit = defineEmits<{
  /** 用這個時段詢問:開詢問表單並預填日期與時段。 */
  inquire: [payload: { date: string, time: string }]
  /** 一起問類似場地:整天沒了時唯一誠實的出口。 */
  'broadcast-inquiry': [payload: { date: string }]
}>()

const {t} = useI18n()
const behaviorFlow = useBehaviorFlow()

const schedule = ref<SpaceSchedule | null>(null)
const currentDate = ref(new Date())
const selectedDate = ref('')
const selectedSlot = ref('')

const SYMBOL_CLASS: Record<ScheduleState, string> = {
  open: 'text-osn-green-4',
  partial: 'text-osn-yellow-00',
  full: 'text-osn-red-00',
  closed: 'text-osn-grey-02'
}

const dayIndex = computed<Record<string, ScheduleDay>>(
    () => indexByDate(schedule.value?.days ?? [])
)

const prevDisabled = computed(() => isPrevMonthDisabled(currentDate.value, new Date()))
const nextDisabled = computed(() => isNextMonthDisabled(currentDate.value, new Date()))

const selectedState = computed<ScheduleState>(() => stateOf(selectedDate.value))
const selectedSlots = computed<ScheduleSlot[]>(() => dayIndex.value[selectedDate.value]?.slots ?? [])

const selectedLabel = computed(() => {
  const [, month, day] = selectedDate.value.split('-')
  const weekday = t('space.schedule_weekdays').charAt(new Date(selectedDate.value).getDay())
  return `${Number(month)}/${Number(day)}（${weekday}）`
})

function stateOf(date: string): ScheduleState {
  return dayState(dayIndex.value[date])
}

function symbolClass(state: ScheduleState): string {
  return SYMBOL_CLASS[state]
}

function dayNumber(date: string): string {
  return String(Number(date.split('-')[2]))
}

function selectDay(date: string): void {
  selectedDate.value = date
  const first = dayIndex.value[date]?.slots?.[0]
  selectedSlot.value = first ? slotTimeValue(first) : ''
  behaviorFlow.startTask(FEATURE_ID, 'date', 'read')
  behaviorFlow.trackStep(FEATURE_ID, 'date', 'attempt')
}

function selectSlot(slot: ScheduleSlot): void {
  selectedSlot.value = slotTimeValue(slot)
  behaviorFlow.trackStep(FEATURE_ID, 'slot', 'attempt')
}

function goMonth(step: number): void {
  if (step < 0 ? prevDisabled.value : nextDisabled.value) {
    return
  }
  const date = currentDate.value
  currentDate.value = new Date(date.getFullYear(), date.getMonth() + step, 1)
}

function goToday(): void {
  currentDate.value = new Date()
}

// 換月才重抓:同一個月裡點日期只是展開下方清單,再打一次 API 只是白跑。
watch(() => monthKey(currentDate.value), (next, previous) => {
  if (next !== previous) {
    selectedDate.value = ''
    load()
  }
})

async function load(): Promise<void> {
  const {startDate, endDate} = monthRange(currentDate.value)
  // 取不到就整塊不出現(httpRequest 已經吐過錯誤訊息)。日曆是加分資訊,
  // 一次查詢失敗不該讓詳情頁看起來壞掉,更不該畫成「整月都沒開」。
  const data = await getSpaceSchedule(props.spaceId, startDate, endDate)
  schedule.value = data ?? null
}

function emitInquire(): void {
  emit('inquire', {date: selectedDate.value, time: selectedSlot.value})
}

function emitBroadcast(): void {
  emit('broadcast-inquiry', {date: selectedDate.value})
}

onMounted(load)
</script>

<style scoped
       lang="scss">
.day-cell {
  height: 100%;
  text-align: center;
}

.day-number {
  font-size: 13px;
  line-height: 1.2;
}

.day-symbol {
  font-size: 16px;
  line-height: 1.2;
}

.day-selected {
  border: 1px solid #2f6b4f;
  border-radius: 6px;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px 0 0;
  margin: 0;
  list-style-type: none;
  font-size: 13px;
  color: #666;
}

.detail {
  margin-top: 12px;
  padding: 12px;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  font-size: 14px;
}

.slots {
  padding: 0;
  margin: 0;
  list-style-type: none;
}

.slot {
  padding: 6px 10px;
  margin-bottom: 6px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  cursor: pointer;
}

.slot-on {
  border-color: #2f6b4f;
  font-weight: 600;
}

.cta {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  margin-top: 10px;
  border-radius: 50px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
}
</style>
